import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DigitalBusinessCard} from '../../../digital-business-card/digital-business-card.model';
import {catchError, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {DigitalBusinessCardsService} from '../../../digital-business-card/digital-business-cards.service';
import {EMPTY, forkJoin, from, Observable, of, Subject, throwError} from 'rxjs';
import {Router} from '@angular/router';
import {AuthService} from '../../../global-services/auth.service';
import {DbcIndexedDbService} from '../../../global-services/indexed-db.service';
import {getRandom5DigitNumber, INDEXDB_DBC_KEYS, ONBOARDING_STORE_NAME, isMobileDevice, extractUserProperties} from '../../dbc-onboard-v2.utils';
import {MediaFolderService} from '../../../media-folders/media-folder.service';
import {MediaService} from '../../../places/media-service';
import {ToastComponent} from '../../../global-components/toast/toast.component';
import {AMPLITUDE_EVENT_CATEGORIES, AMPLITUDE_EVENTS, AmplitudeService} from '../../../global-services/amplitude.service';
import { CookieService } from 'ngx-cookie-service';
import { FEATURE_FLAGS } from 'app/shared/feature-flags';

enum SCREEN_TYPE {
    LOADER,
    ERROR
}

enum ERROR_TYPE {
    UPGRADE_REQUIRED,
    INTERNAL_ERROR,
}

@Component({
    selector: 'app-post-signup',
    templateUrl: './post-signup.component.html',
    styleUrls: ['./post-signup.component.scss']
})
export class PostSignupComponent implements OnDestroy, OnInit {

    dbc: DigitalBusinessCard
    isLoading: boolean = true;
    logosFolderId: number = 0;
    private readonly ngUnsubscribe = new Subject()
    errorMessage: string = '';
    errorTitle: string = '';
    currentScreen = SCREEN_TYPE.LOADER;
    showErrorScreen: boolean = false;
    logoFileName: string = '';
    userImageFileName: string = '';
    isMobileDevice: boolean;
    routeToMyCards: boolean = true;
    primaryCtaText: string;
    isLoggedIn: boolean;

    @ViewChild('customSignupToast') customSignupToast!: ToastComponent;

    constructor(private readonly digitalBusinessCardsService: DigitalBusinessCardsService,
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly indexedDbService: DbcIndexedDbService,
        private readonly mediaFolderService: MediaFolderService,
        private readonly mediaService: MediaService,
        private readonly amplitudeService: AmplitudeService,
        private readonly cookieService: CookieService) {
        this.initializeAndCreateDbc()
    }

    ngOnInit() {
        this.isMobileDevice = isMobileDevice();

        this.authService.isLoggedIn$.subscribe((isLoggedIn) => {
            this.isLoggedIn = isLoggedIn;
        })
        this.sendIntercomCookieDataToAmplitude();
    }

    sendIntercomCookieDataToAmplitude() {
        try {
            const intercomUserAttribute = this.cookieService.get('intercom_user_attribute');
            if (!intercomUserAttribute) {
                return;
            }

            const intercom_user_attribute = JSON.parse(intercomUserAttribute);
            const user_properties = extractUserProperties(intercom_user_attribute);

            if (Object.keys(user_properties).length > 0) {
                this.amplitudeService.setUserProperties(user_properties);
            }

        } catch (error) {
            console.error('Error setting intercom cookie data to amplitude:', error);
        }
    }

    public initializeAndCreateDbc(): void {
        this.amplitudeService.logEvent(AMPLITUDE_EVENT_CATEGORIES.Onboarding, AMPLITUDE_EVENTS.SIGNUP_SUBMITTED, {
            signup_method: 'email'
        });
        this.checkUserLoggedIn().pipe(
            switchMap(({ dbc, isLoggedIn }) => {
                if (!isLoggedIn) {
                    return EMPTY;
                }

                this.dbc = dbc;
                return forkJoin({
                    updatedDbc: this.updateDbcWithUserInfo(this.dbc),
                    mediaUrls: this.getMediaUrls()
                }).pipe(
                    takeUntil(this.ngUnsubscribe) // Unsubscribe from isLoggedIn$ after this point
                );
            }),
            switchMap(({ updatedDbc, mediaUrls }) => {
                this.dbc = updatedDbc;

                // Check if media URLs exist and get folderId only if needed
                if (mediaUrls?.logoUrl || mediaUrls?.userImageUrl) {
                    return forkJoin({
                        folderId: this.getLogosFolderInfo(),
                        mediaUrls: of(mediaUrls)
                    });
                }

                return of({ folderId: null, mediaUrls });
            }),
            switchMap(({ folderId, mediaUrls }) => {
                // Process and upload media only if media URLs exist
                if (mediaUrls?.logoUrl || mediaUrls?.userImageUrl) {
                    this.logosFolderId = folderId;
                    return this.processAndUploadMedia(mediaUrls, this.logosFolderId);
                }

                return of(null);
            }),
            switchMap(() => {
                return forkJoin({
                    saveResult: this.saveDbcToIndexedDb(this.dbc),
                    serverResult: this.createDbcOnServer(this.dbc)
                });
            }),
            catchError(err => {
                return throwError(() => err);
            })
        ).subscribe({
            next: () => {
            },
            complete: () => {
            }
        });
    }

    private processAndUploadMedia(mediaUrls: { logoUrl: string, userImageUrl: string }, folderId: number): Observable<any> {
        if (!folderId) {
            return throwError(() => new Error('Missing folder ID'));
        }

        return this.uploadMedia(
            mediaUrls.logoUrl,
            mediaUrls.userImageUrl,
            folderId
        ).pipe(
            tap(uploadResults => {
                if (uploadResults) {
                    uploadResults.forEach(media => {
                        if (media && media.name === this.logoFileName) {
                            this.dbc.logo_url = media.url;
                        } else if (media && media.name === this.userImageFileName) {
                            this.dbc.user_image_url = media.url;
                        }
                    });
                }
            }),
            catchError(err => {
                this.showErrorMessage(ERROR_TYPE.INTERNAL_ERROR);
                return throwError(() => err);
            })
        );
    }

    private checkUserLoggedIn(): Observable<{ dbc: DigitalBusinessCard, isLoggedIn: boolean }> {
        return this.authService.isLoggedIn$.pipe(
            takeUntil(this.ngUnsubscribe),
            switchMap(isLoggedIn => {
                if (!isLoggedIn) {
                    return of({ dbc: null, isLoggedIn: false });
                }
                return forkJoin({
                    dbc: this.getDbcFromStorage(),
                    isLoggedIn: of(isLoggedIn)
                });
            }),
            catchError(err => {
                this.routeToMyCards = false;
                this.showErrorMessage(ERROR_TYPE.INTERNAL_ERROR);
                return throwError(() => err);
            })
        );
    }

    private getLogosFolderInfo(): Observable<number | null> {
        return this.mediaFolderService.getList(1, 10).pipe(
            map(folders => {
                const logosFolder = folders.objects.find(folder => folder.logos);
                if (!logosFolder) {
                    throw new Error('Logos folder not found');
                }
                return logosFolder.id;
            }),
            catchError(err => {
                this.showErrorMessage(ERROR_TYPE.INTERNAL_ERROR);
                return throwError(() => err);
            })
        );
    }

    private getDbcFromStorage(): Observable<DigitalBusinessCard | null> {
        return this.indexedDbService.getKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.DBC).pipe(
            map(dbc => {
                if (!dbc) {
                    throw new Error('Digital Business Card not found in IndexedDB');
                }
                return new DigitalBusinessCard(dbc);
            }),
            catchError(err => {
                return throwError(() => new Error('Digital Business Card not found in IndexedDB'));
            })
        );
    }

    private updateDbcWithUserInfo(dbc: DigitalBusinessCard): Observable<DigitalBusinessCard> {
        return this.authService.user$.pipe(
            take(1),
            map(user => {
                if (!user?.organization?.id) {
                    throw new Error('User or organization information is missing');
                }
                dbc.organization = user.organization.id;
                dbc.clearModelForPost();
                return dbc;
            }),
            catchError(err => {
                this.showErrorMessage(ERROR_TYPE.INTERNAL_ERROR);
                return throwError(() => err);
            })
        );
    }

    private getMediaUrls(): Observable<{logoUrl: string | null, userImageUrl: string | null}> {
        return forkJoin({
            logoUrl: this.indexedDbService.getKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.LOGO_URL),
            userImageUrl: this.indexedDbService.getKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.USER_IMAGE_URL)
        }).pipe(
            catchError(err => {
                this.showErrorMessage(ERROR_TYPE.INTERNAL_ERROR);
                return throwError(() => err);
            })
        );
    }

    private uploadMedia(logoUrl: string | null, userImageUrl: string | null, folderId: number): Observable<any[]> {
        if (!folderId) {
            return throwError(() => new Error('Invalid folder ID'));
        }

        const uploadLogo$ = logoUrl ? this.uploadSingleFile(logoUrl, folderId, true) : of(null);
        const uploadUserImage$ = userImageUrl ? this.uploadSingleFile(userImageUrl, folderId, false) : of(null);

        return forkJoin([uploadLogo$, uploadUserImage$]).pipe(
            catchError(err => {
                this.showErrorMessage(ERROR_TYPE.INTERNAL_ERROR);
                return throwError(() => err);
            })
        );
    }

    private uploadSingleFile(url: string, folderId: number, isLogo: boolean): Observable<any> {
        return from(fetch(url)).pipe(
            switchMap(response => from(response.blob())),
            switchMap(blob => {
                const extension = blob.type.split('/')[1];
                const fileName = isLogo
                    ? `dbc-logo-${getRandom5DigitNumber()}.${extension}`
                    : `dbc-user-image-${getRandom5DigitNumber()}.${extension}`;

                if (isLogo) {
                    this.logoFileName = fileName;
                } else {
                    this.userImageFileName = fileName;
                }

                const file = new File([blob], fileName, { type: blob.type });
                return from(this.mediaService.uploadFile(file, 'image', false, false, folderId).toPromise());
            }),
            catchError(err => {
                this.showErrorMessage(ERROR_TYPE.INTERNAL_ERROR);
                return throwError(() => err);
            })
        );
    }

    private saveDbcToIndexedDb(dbc: DigitalBusinessCard): Observable<void> {
        return this.indexedDbService.setKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.DBC, dbc).pipe(
            catchError(err => {
                this.showErrorMessage(ERROR_TYPE.INTERNAL_ERROR);
                return throwError(() => err);
            })
        );
    }

    private createDbcOnServer(dbc: DigitalBusinessCard): Observable<DigitalBusinessCard> {
        const [firstName, ...lastNameParts] = dbc.first_name.split(' ');
        dbc.first_name = firstName;
        dbc.last_name = lastNameParts.join(' ');
        return this.digitalBusinessCardsService.post(dbc).pipe(
            tap(res => {
                const card = new DigitalBusinessCard(res);
                this.digitalBusinessCardsService.setDBCCreated(card, 'create');
                card.__show_new_sticky_btn__ = false;
                card.lead_collection = false;
                card.__show_add_to_contacts__ = false;
                card.__zoom__ = 0.7
                this.indexedDbService.setKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.DBC, card).subscribe();
                this.redirectToOnboardShareScreen({dbc_id: card.id});
            }),
            catchError(err => {
                this.getErrorMessage(err.status)
                return throwError(() => err);
            })
        );
    }

    getErrorMessage(status: number) {
        if (status === 426) {
            this.showErrorMessage(ERROR_TYPE.UPGRADE_REQUIRED);
        } else if (status >= 500) {
            this.showErrorMessage(ERROR_TYPE.INTERNAL_ERROR);
        } else {
            this.showErrorMessage(ERROR_TYPE.INTERNAL_ERROR);
        }
    }

    showErrorMessage(errorType: number) {
        this.indexedDbService.clearStore(ONBOARDING_STORE_NAME).subscribe()
        this.currentScreen = SCREEN_TYPE.ERROR;
        switch (errorType) {
            case ERROR_TYPE.INTERNAL_ERROR:
                this.errorTitle = 'Error';
                this.errorMessage = 'Something went wrong. Please try again later, and if the issue persists, contact support.';
                break;
            case ERROR_TYPE.UPGRADE_REQUIRED:
                this.errorTitle = 'Error';
                this.errorMessage = 'A card is already associated with this account.';
                break;
            default:
                this.errorTitle = 'Error';
                this.errorMessage = 'Something went wrong.';
                break;
        }
        if (this.isMobileDevice) {
            this.showErrorScreen = true;
            this.primaryCtaText = this.isLoggedIn ? 'Go to Dashboard' : 'Go Back';
        } else if (!this.isMobileDevice) {
            this.showErrorScreen = false
            this.customSignupToast.type = 'danger';
            this.customSignupToast.message = this.errorMessage;
            this.customSignupToast.showToast(3000);
            setTimeout(() => {
                this.handleErrorRouting()
            }, 3000)
        }
    }

    handleErrorRouting() {
        this.showErrorScreen = false;
        if (this.isLoggedIn) {
            this.routeTheUserToMyCards();
        } else {
            this.routeToOnboardingFlow();
        }
    }

    routeToOnboardingFlow() {
        this.router.navigate(['/digital-business-cards/onboarding']);
    }

    routeTheUserToMyCards() {
        this.router.navigate(['/digital-business-cards/my-cards']);
    }

    redirectToOnboardShareScreen(customParams: any = {}) {
        // if in mobile device, navigate to the share screen that was in the previous onboarding flow
        // else navigate to the overview page
        this.authService.getFeatureFlag(FEATURE_FLAGS.DBC_NEW_SHARE_EXPERIENCE, false).then((res) => {
            // To decide if we should go to overview or my cards on Desktop
            let showNewShareExperienceOnboardingFlow = (res && this.authService.getUser()?.isOnDBCFreemiumPlan())
            if (this.isMobileDevice) {
                this.router.navigate(['/digital-business-cards/onboard'], {
                    queryParams: {
                        newOnboarding: true,
                        ...customParams
                    }
                })
            } else if (showNewShareExperienceOnboardingFlow) {
                this.router.navigate(['/overview'], {
                    queryParams: {
                        ...customParams
                    }
                })
            } else{
                this.router.navigate(['/digital-business-cards/my-cards'], {
                    queryParams: {
                        ...customParams
                    }
                })
            }
        })
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(undefined);
        this.ngUnsubscribe.complete();
    }

    protected readonly SCREEN_TYPE = SCREEN_TYPE;
}
